import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import {
  getVolunteers,
  getAssignments,
  getDrivers,
  getLocations,
  getShiftTimes,
  saveTransportStatus,
  getTransportStatuses,
  saveAssignment,
  getHubOversight,
  getHubHospitality,
} from '../services/firebase';
import './Admin.css';

const DAYS = ['Thu-April-9', 'Fri-April-10', 'Sat-April-11', 'Sun-April-12'];
const DEFAULT_LOCATIONS = [
  'Downtown Augusta',
  'James Brown Blvd',
  'Riverwalk',
  'Surrey Center',
  'Washington Road'
];

function DayOfOperations({ onLogout }) {
  const [volunteers, setVolunteers] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationMap, setLocationMap] = useState({}); // name → address
  const [shiftTimes, setShiftTimes] = useState([]);
  const [transport, setTransport] = useState({});
  const [selectedDay, setSelectedDay] = useState(DAYS[0]);
  const [loading, setLoading] = useState(true);
  const [expandedLocations, setExpandedLocations] = useState({});
  const [expandedShifts, setExpandedShifts] = useState({});
  const [viewMode, setViewMode] = useState('location'); // 'location' | 'time'
  const [liveStatusOpen, setLiveStatusOpen] = useState(false);
  const [movingVolId, setMovingVolId] = useState(null);
  const [movingFrom, setMovingFrom] = useState(null); // { location, shiftTime }
  const [moveToLocation, setMoveToLocation] = useState('');
  const [moveToShift, setMoveToShift] = useState('');
  const [moveSaving, setMoveSaving] = useState(false);

  // Hub data
  const [hubOversight, setHubOversight] = useState(null);
  const [hubHospitality, setHubHospitality] = useState(null);

  // SMS state
  const [smsTarget, setSmsTarget] = useState(null); // { location, shiftTime } | null
  const [smsText, setSmsText] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null); // { sent, failed } | null

  useEffect(() => {
    loadData();
  }, []);

  // Reload hub data whenever selected day changes
  useEffect(() => {
    if (!selectedDay) return;
    const loadHubData = async () => {
      const [oversight, hospitality] = await Promise.all([
        getHubOversight(selectedDay),
        getHubHospitality(selectedDay),
      ]);
      setHubOversight(oversight || null);
      setHubHospitality(hospitality || null);
    };
    loadHubData();
  }, [selectedDay]);

  const loadData = async () => {
    setLoading(true);
    const [volData, assignmentData, driverData, locationData, transportData, shiftTimeData] = await Promise.all([
      getVolunteers(),
      getAssignments(),
      getDrivers(),
      getLocations(),
      getTransportStatuses(),
      getShiftTimes(),
    ]);

    setVolunteers(volData);
    setDrivers(driverData);
    setShiftTimes(shiftTimeData);

    const activeLocationObjs = (locationData.length > 0
      ? locationData.filter(l => l.active)
      : DEFAULT_LOCATIONS.map(name => ({ name, address: '' }))
    ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    const activeLocations = activeLocationObjs.map(l => l.name);
    setLocations(activeLocations);

    const lmap = {};
    activeLocationObjs.forEach(l => { lmap[l.name] = l.address || ''; });
    setLocationMap(lmap);

    const assignMap = {};
    assignmentData.forEach(a => {
      assignMap[`${a.day}_${a.location}_${a.shiftTime}`] = a.volunteers || [];
    });
    setAssignments(assignMap);

    const transportMap = {};
    transportData.forEach(t => {
      // Backward compat: migrate old dropoffDriver1/2 format to arrays
      const dropoffDrivers = t.dropoffDrivers || [t.dropoffDriver1, t.dropoffDriver2].filter(Boolean);
      const pickupDrivers = t.pickupDrivers || [t.pickupDriver1, t.pickupDriver2].filter(Boolean);
      transportMap[`${t.day}_${t.location}_${t.shiftTime}`] = { ...t, dropoffDrivers, pickupDrivers };
    });
    setTransport(transportMap);

    // Default: all locations expanded, all shift times expanded
    const expandedLoc = {};
    activeLocations.forEach(loc => { expandedLoc[loc] = true; });
    setExpandedLocations(expandedLoc);

    const expandedShiftMap = {};
    shiftTimeData.filter(s => s.active !== false).forEach(s => { expandedShiftMap[s.name] = true; });
    setExpandedShifts(expandedShiftMap);

    setLoading(false);
  };

  const getVolunteer = (id) => volunteers.find(v => v.id === id);

  // Returns driver IDs already assigned at other locations for this day+shift
  const getBusyDriverIds = (day, shiftTime, excludeLocation) => {
    const busy = new Set();
    Object.values(transport).forEach(td => {
      if (td.day !== day || td.shiftTime !== shiftTime || td.location === excludeLocation) return;
      (td.dropoffDrivers || []).forEach(id => id && busy.add(id));
      (td.pickupDrivers || []).forEach(id => id && busy.add(id));
    });
    return busy;
  };

  // Sum passenger capacity of an array of driver IDs
  const calcCapacity = (driverIds) =>
    (driverIds || []).reduce((sum, id) => {
      if (!id) return sum;
      const d = drivers.find(dr => dr.id === id);
      return sum + (d?.passengerCapacity ? Number(d.passengerCapacity) : 0);
    }, 0);

  // Returns true if this volunteer has a shift in the NEXT consecutive time slot on the same day (any location)
  const isBackToBack = (volunteerId, day, shiftTime) => {
    const names = shiftTimes.filter(s => !s.days || s.days.includes(day)).map(s => s.name);
    const idx = names.indexOf(shiftTime);
    if (idx === -1 || idx === names.length - 1) return false;
    const nextShift = names[idx + 1];
    for (const loc of locations) {
      const key = `${day}_${loc}_${nextShift}`;
      if ((assignments[key] || []).includes(volunteerId)) return true;
    }
    return false;
  };

  // Returns true if this volunteer also had a shift in the PREVIOUS consecutive time slot on the same day
  const isCarryOver = (volunteerId, day, shiftTime) => {
    const names = shiftTimes.filter(s => !s.days || s.days.includes(day)).map(s => s.name);
    const idx = names.indexOf(shiftTime);
    if (idx <= 0) return false;
    const prevShift = names[idx - 1];
    for (const loc of locations) {
      const key = `${day}_${loc}_${prevShift}`;
      if ((assignments[key] || []).includes(volunteerId)) return true;
    }
    return false;
  };

  const startMove = (volId, location, shiftTime) => {
    setMovingVolId(volId);
    setMovingFrom({ location, shiftTime });
    setMoveToLocation(location);
    setMoveToShift(shiftTime);
  };

  const cancelMove = () => {
    setMovingVolId(null);
    setMovingFrom(null);
    setMoveToLocation('');
    setMoveToShift('');
  };

  const confirmMove = async () => {
    if (!movingVolId || !movingFrom) return;
    setMoveSaving(true);
    const day = selectedDay;
    const fromKey = `${day}_${movingFrom.location}_${movingFrom.shiftTime}`;
    const toKey = `${day}_${moveToLocation}_${moveToShift}`;

    if (fromKey !== toKey) {
      const fromVols = (assignments[fromKey] || []).filter(id => id !== movingVolId);

      // Place volunteer in the correct slot range based on their type
      const vol = volunteers.find(v => v.id === movingVolId);
      const isSmpw = vol?.volunteerType === 'smpw';
      const toVols = [...(assignments[toKey] || [])];
      while (toVols.length < 5) toVols.push(''); // ensure at least 5 slots

      if (!toVols.includes(movingVolId)) {
        const range = isSmpw ? [0, 1, 2] : [3, 4];
        const placed = range.some(i => {
          if (!toVols[i]) { toVols[i] = movingVolId; return true; }
          return false;
        });
        if (!placed) toVols.push(movingVolId); // overflow into extra slot
      }

      setAssignments(prev => ({ ...prev, [fromKey]: fromVols, [toKey]: toVols }));
      await saveAssignment({ day, location: movingFrom.location, shiftTime: movingFrom.shiftTime, volunteers: fromVols });
      await saveAssignment({ day, location: moveToLocation, shiftTime: moveToShift, volunteers: toVols });
    }

    cancelMove();
    setMoveSaving(false);
  };

  const openSms = (location, shiftTime, volDetails) => {
    const day = selectedDay;
    const template = `[SMPW Masters Event] Reminder: Your shift is ${shiftTime} on ${day.replace(/-/g, ' ')} at ${location}. Please arrive at the Kingdom Hall 1 hour before your shift for the shuttle. Questions? Contact your coordinator.`;
    setSmsTarget({ location, shiftTime, volDetails });
    setSmsText(template);
    setSmsResult(null);
  };

  const closeSms = () => {
    setSmsTarget(null);
    setSmsText('');
    setSmsResult(null);
  };

  const sendSms = async () => {
    if (!smsTarget || smsSending) return;
    const recipients = smsTarget.volDetails
      .map(vd => ({ name: `${vd.vol.lastName}, ${vd.vol.firstName}`, phone: vd.vol.phone }))
      .filter(r => r.phone);

    if (recipients.length === 0) {
      alert('No phone numbers available for this shift.');
      return;
    }

    setSmsSending(true);
    setSmsResult(null);
    try {
      const res = await fetch('/api/send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients, message: smsText }),
      });
      const data = await res.json();
      setSmsResult(data);
    } catch (err) {
      setSmsResult({ success: false, error: err.message });
    } finally {
      setSmsSending(false);
    }
  };

  const handleTransportChange = async (day, location, shiftTime, field, value) => {
    const key = `${day}_${location}_${shiftTime}`;
    const current = transport[key] || {};
    const updated = { ...current, [field]: value };
    setTransport(prev => ({ ...prev, [key]: updated }));
    await saveTransportStatus(day, location, shiftTime, updated);
  };

  const handleDriverChange = async (day, location, shiftTime, type, idx, value) => {
    const key = `${day}_${location}_${shiftTime}`;
    const current = transport[key] || {};
    const arr = [...(current[type] || [''])];
    arr[idx] = value;
    const updated = { ...current, [type]: arr };
    setTransport(prev => ({ ...prev, [key]: updated }));
    await saveTransportStatus(day, location, shiftTime, updated);
  };

  const handleAddDriver = async (day, location, shiftTime, type) => {
    const key = `${day}_${location}_${shiftTime}`;
    const current = transport[key] || {};
    const arr = [...(current[type] || ['']), ''];
    const updated = { ...current, [type]: arr };
    setTransport(prev => ({ ...prev, [key]: updated }));
    await saveTransportStatus(day, location, shiftTime, updated);
  };

  const handleRemoveDriver = async (day, location, shiftTime, type, idx) => {
    const key = `${day}_${location}_${shiftTime}`;
    const current = transport[key] || {};
    const arr = (current[type] || []).filter((_, i) => i !== idx);
    const updated = { ...current, [type]: arr };
    setTransport(prev => ({ ...prev, [key]: updated }));
    await saveTransportStatus(day, location, shiftTime, updated);
  };

  const getDayStats = (day) => {
    let assigned = 0;
    let checkedIn = 0;
    for (const loc of locations) {
      for (const shiftObj of shiftTimes.filter(s => !s.days || s.days.includes(day))) {
        const shift = shiftObj.name;
        const key = `${day}_${loc}_${shift}`;
        const shiftId = key.replace(/\s+/g, '_');
        const vols = (assignments[key] || []).filter(Boolean);
        assigned += vols.length;
        for (const id of vols) {
          const vol = getVolunteer(id);
          if (vol && vol[`checkIn_${shiftId}`] === 'checked-in') checkedIn++;
        }
      }
    }
    return { assigned, checkedIn, notIn: assigned - checkedIn };
  };

  if (loading) {
    return <div className="admin-container"><div className="loading">Loading...</div></div>;
  }

  const stats = getDayStats(selectedDay);

  // Sorted drivers list for dropdowns
  const sortedDrivers = [...drivers].sort(
    (a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '')
  );

  const driverLabel = (d) => {
    const vehicle = [d.vehicleMake, d.vehicleModel, d.vehicleColor].filter(Boolean).join(' ') || d.vehicleType || 'Vehicle';
    return `${d.lastName}, ${d.firstName} — ${vehicle} (${d.passengerCapacity ?? 0} pass)`;
  };

  const dayShifts = shiftTimes.filter(s => !s.days || s.days.includes(selectedDay));

  // ─── Render a single shift block (shared by both view modes) ───────────────
  // showLocationLabel = true in "By Time" view to identify which location the block belongs to
  const renderShiftBlock = (location, shiftTime, showLocationLabel = false) => {
    const key = `${selectedDay}_${location}_${shiftTime}`;
    const shiftId = key.replace(/\s+/g, '_');
    const assigned = (assignments[key] || []).filter(Boolean);
    if (assigned.length === 0) return null;

    const td = transport[key] || {};
    const pickupDone = td.pickupDone || false;
    const smpwCount = Math.max(0, Number(td.smpwCount) || 0);
    const totalVolCount = assigned.length + smpwCount;

    const volDetails = assigned.map(id => {
      const vol = getVolunteer(id);
      if (!vol) return null;
      const checkInStatus = vol[`checkIn_${shiftId}`] || 'pending';
      const b2b = isBackToBack(id, selectedDay, shiftTime);
      const carry = isCarryOver(id, selectedDay, shiftTime);
      return { vol, checkInStatus, b2b, carry };
    }).filter(Boolean);

    // Volunteers to pick up = checked-in and NOT staying for next shift
    const pickupCount = volDetails.filter(vd =>
      vd.checkInStatus === 'checked-in' && !vd.b2b
    ).length;
    const stayingCount = volDetails.filter(vd =>
      vd.b2b && vd.checkInStatus === 'checked-in'
    ).length;

    // Busy driver IDs at other locations this shift
    const busyDriverIds = getBusyDriverIds(selectedDay, shiftTime, location);

    // Capacity calculations — use totalVolCount (includes SMPW) for drop-off
    const dropoffCapacity = calcCapacity(td.dropoffDrivers?.filter(Boolean));
    const pickupCapacity = calcCapacity(td.pickupDrivers?.filter(Boolean));

    const address = locationMap[location] || '';

    return (
      <div
        key={`${location}_${shiftTime}`}
        className={`dayof-shift-block${pickupDone ? ' dayof-shift-done' : ''}`}
      >
        {/* Location label — shown only in By Time view */}
        {showLocationLabel && (
          <div className="dayof-loc-in-time-view">
            📍 {location}
            {address && (
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="dayof-loc-address-link"
              >
                {address} ↗
              </a>
            )}
          </div>
        )}

        {/* Shift header */}
        <div className="dayof-shift-header">
          <span className="shift-time-label">⏰ {shiftTime}</span>
          <span className="dayof-vol-count">
            {totalVolCount} volunteer{totalVolCount !== 1 ? 's' : ''}
            {smpwCount > 0 && (
              <span className="dayof-smpw-note"> ({assigned.length} local + {smpwCount} SMPW)</span>
            )}
          </span>
          {hubOversight?.shifts?.[shiftTime]?.prayer && (
            <span className="hub-shift-badge prayer">🙏 {hubOversight.shifts[shiftTime].prayer}</span>
          )}
          {hubOversight?.shifts?.[shiftTime]?.spiritualPoint && (
            <span className="hub-shift-badge spiritual">✨ {hubOversight.shifts[shiftTime].spiritualPoint}</span>
          )}
          {pickupDone && (
            <span className="dayof-done-badge">✅ All Picked Up</span>
          )}
          <button
            className="dayof-sms-btn"
            onClick={() => {
              const isOpen = smsTarget?.location === location && smsTarget?.shiftTime === shiftTime;
              isOpen ? closeSms() : openSms(location, shiftTime, volDetails);
            }}
          >
            {smsTarget?.location === location && smsTarget?.shiftTime === shiftTime ? 'Close' : '💬 Text Shift'}
          </button>
        </div>

        {/* SMPW count row */}
        <div className="dayof-smpw-row">
          <label className="dayof-smpw-label">SMPW volunteers:</label>
          <input
            type="number"
            min="0"
            max="99"
            className="dayof-smpw-input"
            value={smpwCount === 0 ? '' : smpwCount}
            placeholder="0"
            onChange={e => handleTransportChange(
              selectedDay, location, shiftTime, 'smpwCount',
              e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0)
            )}
          />
          {smpwCount > 0 && (
            <span className="dayof-total-vol-label">
              = {totalVolCount} total needing transport
            </span>
          )}
        </div>

        {/* SMS compose panel */}
        {smsTarget?.location === location && smsTarget?.shiftTime === shiftTime && (
          <div className="dayof-sms-panel">
            <div className="dayof-sms-recipients">
              <strong>Recipients ({volDetails.filter(vd => vd.vol.phone).length} with phone):</strong>
              <span className="dayof-sms-names">
                {volDetails.map(vd => vd.vol.phone
                  ? `${vd.vol.lastName}, ${vd.vol.firstName}`
                  : `${vd.vol.lastName}, ${vd.vol.firstName} (no phone)`
                ).join(' · ')}
              </span>
            </div>
            <textarea
              className="dayof-sms-textarea"
              value={smsText}
              onChange={e => setSmsText(e.target.value)}
              rows={4}
              placeholder="Type your message here..."
              maxLength={160}
            />
            <div className="dayof-sms-footer">
              <span className="dayof-sms-charcount">{smsText.length}/160 chars</span>
              {smsResult && (
                <span className={`dayof-sms-result${smsResult.success ? ' success' : ' error'}`}>
                  {smsResult.success
                    ? `✓ Sent to ${smsResult.sent}${smsResult.failed?.length ? `, ${smsResult.failed.length} failed` : ''}`
                    : `✗ ${smsResult.error || 'Send failed'}`}
                </span>
              )}
              <button
                className="btn btn-primary dayof-sms-send"
                onClick={sendSms}
                disabled={smsSending || !smsText.trim()}
              >
                {smsSending ? 'Sending…' : `Send to ${volDetails.filter(vd => vd.vol.phone).length}`}
              </button>
            </div>
          </div>
        )}

        {/* Volunteer list */}
        <div className="dayof-vol-list">
          {volDetails.map(({ vol, checkInStatus, b2b, carry }) => (
            <React.Fragment key={vol.id}>
              <div
                className={`dayof-vol-row${checkInStatus === 'checked-in' ? ' dayof-vol-checkedin' : ''}${checkInStatus === 'no-show' ? ' dayof-vol-noshow' : ''}${movingVolId === vol.id ? ' dayof-vol-moving' : ''}`}
              >
                <div className="dayof-vol-info">
                  <span className="dayof-vol-name">
                    {vol.lastName}, {vol.firstName}
                    {vol.volunteerType === 'smpw' && ' 🔵'}
                    {vol.isKeyman && ' 🔑'}
                    {vol.isDeafHH && <span className="roster-badge badge-deaf" title="Deaf / Hard of Hearing — do not call" style={{ marginLeft: 4, fontSize: '10px' }}>🦻</span>}
                  </span>
                  {vol.phone && !vol.isDeafHH && (
                    <a href={`tel:${vol.phone}`} className="dayof-phone">{vol.phone}</a>
                  )}
                  {vol.phone && vol.isDeafHH && (
                    <span className="dayof-phone" style={{ color: '#7c3aed', cursor: 'default' }} title="Text only — do not call">✉ {vol.phone}</span>
                  )}
                </div>
                <div className="dayof-vol-flags">
                  {checkInStatus === 'checked-in' && (
                    <span className="dayof-badge dayof-badge-in">✓ In</span>
                  )}
                  {checkInStatus === 'no-show' && (
                    <span className="dayof-badge dayof-badge-noshow">✗ No Show</span>
                  )}
                  {b2b && (
                    <span className="dayof-badge dayof-badge-b2b">🔄 Staying</span>
                  )}
                  {carry && (
                    <span className="dayof-badge dayof-badge-carry">⬆ Carry-over</span>
                  )}
                  <button
                    className="dayof-move-btn"
                    onClick={() => movingVolId === vol.id ? cancelMove() : startMove(vol.id, location, shiftTime)}
                  >
                    {movingVolId === vol.id ? 'Cancel' : 'Move'}
                  </button>
                </div>
              </div>
              {movingVolId === vol.id && (
                <div className="dayof-move-ui">
                  <span className="dayof-move-label">Move to:</span>
                  <select
                    value={moveToLocation}
                    onChange={e => setMoveToLocation(e.target.value)}
                  >
                    {locations.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                  <select
                    value={moveToShift}
                    onChange={e => setMoveToShift(e.target.value)}
                  >
                    {shiftTimes.filter(s => !s.days || s.days.includes(selectedDay)).map(s => (
                      <option key={s.name} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary dayof-move-confirm"
                    onClick={confirmMove}
                    disabled={moveSaving}
                  >
                    {moveSaving ? 'Saving…' : 'Confirm Move'}
                  </button>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Transportation */}
        <div className="dayof-transport">
          {/* Drop-off */}
          <div className="dayof-transport-section">
            <div className="dayof-transport-label">
              🚐 Drop-off
              {td.dropoffDrivers?.some(Boolean) && dropoffCapacity > 0 && (
                <span className={`capacity-badge ${dropoffCapacity >= totalVolCount ? 'ok' : 'short'}`}>
                  {dropoffCapacity >= totalVolCount
                    ? `✓ ${dropoffCapacity} seats / ${totalVolCount} vol`
                    : `⚠ ${dropoffCapacity} seats / ${totalVolCount} vol — need more`}
                </span>
              )}
            </div>
            <div className="dayof-driver-slots">
              {(td.dropoffDrivers?.length ? td.dropoffDrivers : ['']).map((driverId, i) => (
                <div key={i} className="dayof-driver-slot">
                  <label>Driver {i + 1}:</label>
                  <select
                    value={driverId || ''}
                    onChange={e => handleDriverChange(
                      selectedDay, location, shiftTime, 'dropoffDrivers', i, e.target.value
                    )}
                  >
                    <option value="">— Select Driver —</option>
                    {sortedDrivers.map(d => (
                      <option key={d.id} value={d.id}>{driverLabel(d)}</option>
                    ))}
                  </select>
                  {driverId && busyDriverIds.has(driverId) && (
                    <div className="conflict-warning" style={{ fontSize: '11px', marginTop: '2px' }}>
                      ⚠️ Driver already assigned at another location this shift
                    </div>
                  )}
                  {i > 0 && (
                    <button
                      className="dayof-driver-remove-btn"
                      onClick={() => handleRemoveDriver(selectedDay, location, shiftTime, 'dropoffDrivers', i)}
                      title="Remove driver"
                    >×</button>
                  )}
                </div>
              ))}
              <button
                className="dayof-add-driver-btn"
                onClick={() => handleAddDriver(selectedDay, location, shiftTime, 'dropoffDrivers')}
              >+ Add Driver</button>
              <label className="dayof-done-check">
                <input
                  type="checkbox"
                  checked={td.dropoffDone || false}
                  onChange={e => handleTransportChange(
                    selectedDay, location, shiftTime, 'dropoffDone', e.target.checked
                  )}
                />
                <span>Drop-off complete</span>
              </label>
            </div>
          </div>

          {/* Pick-up */}
          <div className="dayof-transport-section">
            <div className="dayof-transport-label">
              🚐 Pick-up
              {pickupCount > 0 && (
                <span className="dayof-pickup-count">
                  {' '}({pickupCount} to pick up
                  {stayingCount > 0 && ` — ${stayingCount} staying for next shift`})
                </span>
              )}
              {td.pickupDrivers?.some(Boolean) && pickupCapacity > 0 && (
                <span className={`capacity-badge ${pickupCapacity >= pickupCount ? 'ok' : 'short'}`}>
                  {pickupCapacity >= pickupCount
                    ? `✓ ${pickupCapacity} seats / ${pickupCount} to pick up`
                    : `⚠ ${pickupCapacity} seats / ${pickupCount} to pick up — need more`}
                </span>
              )}
            </div>
            <div className="dayof-driver-slots">
              {(td.pickupDrivers?.length ? td.pickupDrivers : ['']).map((driverId, i) => (
                <div key={i} className="dayof-driver-slot">
                  <label>Driver {i + 1}:</label>
                  <select
                    value={driverId || ''}
                    onChange={e => handleDriverChange(
                      selectedDay, location, shiftTime, 'pickupDrivers', i, e.target.value
                    )}
                  >
                    <option value="">— Select Driver —</option>
                    {sortedDrivers.map(d => (
                      <option key={d.id} value={d.id}>{driverLabel(d)}</option>
                    ))}
                  </select>
                  {driverId && busyDriverIds.has(driverId) && (
                    <div className="conflict-warning" style={{ fontSize: '11px', marginTop: '2px' }}>
                      ⚠️ Driver already assigned at another location this shift
                    </div>
                  )}
                  {i > 0 && (
                    <button
                      className="dayof-driver-remove-btn"
                      onClick={() => handleRemoveDriver(selectedDay, location, shiftTime, 'pickupDrivers', i)}
                      title="Remove driver"
                    >×</button>
                  )}
                </div>
              ))}
              <button
                className="dayof-add-driver-btn"
                onClick={() => handleAddDriver(selectedDay, location, shiftTime, 'pickupDrivers')}
              >+ Add Driver</button>
              <label className="dayof-done-check">
                <input
                  type="checkbox"
                  checked={pickupDone}
                  onChange={e => handleTransportChange(
                    selectedDay, location, shiftTime, 'pickupDone', e.target.checked
                  )}
                />
                <span>All picked up</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Live status summary grid for the selected day ─────────────────────────
  const renderLiveStatus = () => {
    const cells = [];
    dayShifts.forEach(shiftObj => {
      locations.forEach(loc => {
        const key = `${selectedDay}_${loc}_${shiftObj.name}`;
        const localCount = (assignments[key] || []).filter(Boolean).length;
        if (localCount === 0) return;
        const td = transport[key] || {};
        const smpwCount = Math.max(0, Number(td.smpwCount) || 0);
        const total = localCount + smpwCount;
        let status, statusLabel;
        if (td.pickupDone) {
          status = 'complete';
          statusLabel = `✅ Picked up`;
        } else if (td.dropoffDone) {
          status = 'inprogress';
          statusLabel = `🟡 Dropped off`;
        } else {
          status = 'notstarted';
          statusLabel = `⬜ Not started`;
        }
        cells.push({ shiftTime: shiftObj.name, loc, total, smpwCount, localCount, status, statusLabel });
      });
    });

    if (cells.length === 0) {
      return <p style={{ color: '#64748b', fontSize: '13px', margin: '8px 0' }}>No assignments for this day yet.</p>;
    }

    return (
      <div className="dayof-live-grid">
        {cells.map(cell => (
          <div key={`${cell.loc}_${cell.shiftTime}`} className={`dayof-live-cell dayof-live-${cell.status}`}>
            <div className="dayof-live-shift">{cell.shiftTime}</div>
            <div className="dayof-live-loc">{cell.loc}</div>
            <div className="dayof-live-count">
              {cell.total} vol
              {cell.smpwCount > 0 && <span style={{ fontSize: '10px', opacity: 0.8 }}> ({cell.localCount}+{cell.smpwCount})</span>}
            </div>
            <div className="dayof-live-status-label">{cell.statusLabel}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Day-Of Operations</h1>
        <p className="page-subtitle">Monitor real-time status of cart shifts, transport, and volunteer check-ins on event day.</p>
      </header>

      <div className="dayof-content">
        {/* Day selector */}
        <div className="day-selector">
          {DAYS.map(day => (
            <button
              key={day}
              className={`day-tab ${selectedDay === day ? 'active' : ''}`}
              onClick={() => setSelectedDay(day)}
            >
              {day}
            </button>
          ))}
        </div>

        {/* Stats bar */}
        <div className="dayof-stats">
          <div className="stat-card">
            <div className="stat-label">Assigned (Local)</div>
            <div className="stat-value">{stats.assigned}</div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Checked In</div>
            <div className="stat-value">{stats.checkedIn}</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-label">Not Yet In</div>
            <div className="stat-value">{stats.notIn}</div>
          </div>
        </div>

        {/* Hub info panel */}
        {(hubOversight?.schedule?.length > 0 || hubHospitality?.congregation) && (
          <div className="hub-info-panel">
            {hubOversight?.schedule?.length > 0 && (
              <div>
                <strong>On-Site Team:</strong>{' '}
                {hubOversight.schedule.map(p =>
                  `${p.name}${p.fromTime ? ` (${p.fromTime}–${p.toTime || '?'})` : ''}`
                ).join(' · ')}
              </div>
            )}
            {hubHospitality?.congregation && (
              <div style={{ marginTop: hubOversight?.schedule?.length > 0 ? '4px' : 0 }}>
                <strong>Hospitality:</strong>{' '}
                {hubHospitality.congregation}
                {hubHospitality.meals?.length > 0 && ` — ${hubHospitality.meals.join(', ')}`}
              </div>
            )}
          </div>
        )}

        {/* Live Status Panel */}
        <div className="dayof-live-status-panel">
          <div
            className="dayof-live-status-header"
            onClick={() => setLiveStatusOpen(o => !o)}
          >
            <span className="expand-icon">{liveStatusOpen ? '▼' : '▶'}</span>
            <span>Live Status — {selectedDay.replace(/-/g, ' ')}</span>
          </div>
          {liveStatusOpen && (
            <div className="dayof-live-status-body">
              {renderLiveStatus()}
            </div>
          )}
        </div>

        {/* View toggle + Expand / Collapse All */}
        <div className="schedule-controls">
          <div className="dayof-view-toggle">
            <button
              className={`dayof-view-btn${viewMode === 'location' ? ' active' : ''}`}
              onClick={() => setViewMode('location')}
            >
              By Location
            </button>
            <button
              className={`dayof-view-btn${viewMode === 'time' ? ' active' : ''}`}
              onClick={() => setViewMode('time')}
            >
              By Time
            </button>
          </div>
          <button
            onClick={() => {
              if (viewMode === 'location') {
                const e = {}; locations.forEach(l => { e[l] = true; }); setExpandedLocations(e);
              } else {
                const e = {}; dayShifts.forEach(s => { e[s.name] = true; }); setExpandedShifts(e);
              }
            }}
            className="btn btn-secondary"
          >
            ⬇️ Expand All
          </button>
          <button
            onClick={() => {
              if (viewMode === 'location') {
                const e = {}; locations.forEach(l => { e[l] = false; }); setExpandedLocations(e);
              } else {
                const e = {}; dayShifts.forEach(s => { e[s.name] = false; }); setExpandedShifts(e);
              }
            }}
            className="btn btn-secondary"
          >
            ⬆️ Collapse All
          </button>
        </div>

        {/* ── By Location view ─────────────────────────────────────────────── */}
        {viewMode === 'location' && (
          <div className="dayof-grid">
            {locations.map(location => {
              const hasAssignments = dayShifts.some(s =>
                (assignments[`${selectedDay}_${location}_${s.name}`] || []).filter(Boolean).length > 0
              );
              if (!hasAssignments) return null;

              const isExpanded = expandedLocations[location] !== false;
              const address = locationMap[location] || '';

              return (
                <div key={location} className="location-section">
                  <div
                    className="location-header-clickable"
                    onClick={() => setExpandedLocations(prev => ({ ...prev, [location]: !isExpanded }))}
                  >
                    <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                    <h3 className="location-header-title">📍 {location}</h3>
                    {address && (
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '12px', color: '#667eea', marginLeft: '10px', fontWeight: 400 }}
                        onClick={e => e.stopPropagation()}
                      >
                        {address} ↗
                      </a>
                    )}
                  </div>
                  {isExpanded && (
                    <div className="shifts-container">
                      {dayShifts.map(shiftObj => renderShiftBlock(location, shiftObj.name, false))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── By Time view ─────────────────────────────────────────────────── */}
        {viewMode === 'time' && (
          <div className="dayof-grid">
            {dayShifts.map(shiftObj => {
              const shiftTime = shiftObj.name;
              const locsWithAssignments = locations.filter(loc =>
                (assignments[`${selectedDay}_${loc}_${shiftTime}`] || []).filter(Boolean).length > 0
              );
              if (locsWithAssignments.length === 0) return null;

              const isExpanded = expandedShifts[shiftTime] !== false;

              // Summary counts for the collapsed header
              const totalVols = locsWithAssignments.reduce((sum, loc) => {
                const key = `${selectedDay}_${loc}_${shiftTime}`;
                const td = transport[key] || {};
                return sum + (assignments[key] || []).filter(Boolean).length + (Math.max(0, Number(td.smpwCount) || 0));
              }, 0);

              return (
                <div key={shiftTime} className="location-section">
                  <div
                    className="location-header-clickable"
                    onClick={() => setExpandedShifts(prev => ({ ...prev, [shiftTime]: !isExpanded }))}
                  >
                    <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                    <h3 className="location-header-title">⏰ {shiftTime}</h3>
                    <span style={{ fontSize: '13px', color: '#64748b', marginLeft: '10px', fontWeight: 400 }}>
                      {locsWithAssignments.length} location{locsWithAssignments.length !== 1 ? 's' : ''} · {totalVols} vol total
                    </span>
                  </div>
                  {isExpanded && (
                    <div className="shifts-container">
                      {locsWithAssignments.map(loc => renderShiftBlock(loc, shiftTime, true))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default DayOfOperations;
